import { connectMainSocket } from '../../clients/mainSocketClient'
import { MAIN_SOCKET_API_EVENTS } from '../../constants/api/mainSocket'
import { REACT_APP_VAPID_PUBLIC_KEY } from '../../config/runtimeEnv'

const SW_PATH = '/sw.js'

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export const isBrowserPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

export const getVapidPublicKey = () => REACT_APP_VAPID_PUBLIC_KEY || ''

export const registerServiceWorker = async () => {
  if (!isBrowserPushSupported()) return null
  const registration = await navigator.serviceWorker.register(SW_PATH)
  await navigator.serviceWorker.ready
  return registration
}

const emitBrowserEndpoint = (event, subscription) =>
  new Promise((resolve, reject) => {
    connectMainSocket()
      .then(socket => {
        socket.emit(event, subscription.toJSON(), (error, response) => {
          if (error) reject(new Error(typeof error === 'string' ? error : error?.message || 'Push subscription failed'))
          else resolve(response)
        })
      })
      .catch(reject)
  })

/**
 * Subscribe the current browser to Web Push and persist the endpoint on the server.
 * Requires Notification permission and a configured VAPID public key.
 */
export const subscribeBrowserPush = async () => {
  if (!isBrowserPushSupported()) return null

  const vapidPublicKey = getVapidPublicKey()
  if (!vapidPublicKey) {
    console.warn('[browserPush] REACT_APP_VAPID_PUBLIC_KEY is not configured; skipping push subscribe.')
    return null
  }

  let permission = window.Notification.permission
  if (permission !== 'granted') {
    permission = await window.Notification.requestPermission()
  }
  if (permission !== 'granted') return null

  const registration = await registerServiceWorker()
  if (!registration) return null

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    })
  }

  await emitBrowserEndpoint(MAIN_SOCKET_API_EVENTS.USERS_ADD_BROWSER_ENDPOINT, subscription)
  return subscription
}

/** Remove the current browser push subscription locally and on the server. */
export const unsubscribeBrowserPush = async () => {
  if (!isBrowserPushSupported()) return

  const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
  const subscription = registration ? await registration.pushManager.getSubscription() : null
  if (!subscription) return

  try {
    await emitBrowserEndpoint(MAIN_SOCKET_API_EVENTS.USERS_REMOVE_BROWSER_ENDPOINT, subscription)
  } catch (err) {
    console.warn('[browserPush] remove_browser_endpoint failed:', err?.message || err)
  }

  await subscription.unsubscribe()
}

/** Sync push subscription with the profile browser-notification toggle. */
export const syncBrowserPushWithPreference = async enabled => {
  if (enabled) {
    return subscribeBrowserPush()
  }
  return unsubscribeBrowserPush()
}
